import {
  COMMAND_PRIORITY_NORMAL,
  PASTE_COMMAND,
  $createTextNode,
  $insertNodes,
} from "lexical";
import { useCallback, useEffect } from "react";
import * as React from "react";
import { $createInlineImageNode } from "./ImageNode";
import { PasteIcon } from "../../../assets/icons/SvgIcons";
import { LexicalProps } from "../LexicalEditor.types";

export default function Paste({
  editor,
  isDropdown,
}: LexicalProps): JSX.Element {
  const handlePaste = useCallback(
    async (clipboardData: DataTransfer) => {
      const allData: string[] = [];
      const items = clipboardData.items;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.startsWith("image")) {
          const file = item.getAsFile();
          if (file) {
            const src = URL.createObjectURL(file);
            const imageNode = $createInlineImageNode({
              src,
              altText: "Pasted Image",
              showCaption: false,
              position: "full",
            });
            editor.update(() => {
              $insertNodes([imageNode]);
            });
            return;
          }
        }
      }

      const textData = clipboardData.getData("text/plain");
      if (textData) {
        editor.update(() => {
          $insertNodes([$createTextNode(textData)]);
        });
        allData.push(`Text: ${textData}`);
      }
    },
    [editor]
  );

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        if (event.clipboardData) {
          handlePaste(event.clipboardData);
        }
        return true;
      },
      COMMAND_PRIORITY_NORMAL
    );
  }, [editor, handlePaste]);

  const handlePasteButtonClick = () => {
    if (!editor) {
      console.error("Editor not found!");
      return;
    }

    navigator.clipboard
      .read()
      .then(async (clipboardItems) => {
        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            if (type.startsWith("image/")) {
              const blob = await clipboardItem.getType(type);
              const src = URL.createObjectURL(blob);

              editor.update(() => {
                const imageNode = $createInlineImageNode({
                  src,
                  altText: "Pasted Image",
                  showCaption: false,
                  position: "full",
                });
                $insertNodes([imageNode]);
              });

              return;
            }

            if (type === "text/plain") {
              const textData = await clipboardItem.getType(type);
              const text = await textData.text();

              editor.update(() => {
                $insertNodes([$createTextNode(text)]);
              });
            }
          }
        }
      })
      .catch((err) => {
        console.error("Error reading clipboard contents:", err);
      });
  };

  return (
    <div
      onClick={handlePasteButtonClick}
      className={`lexical-toolbarItem ${
        isDropdown ? "lexical-dropdownItem" : ""
      }`}
    >
      <div className="lexical-shortcutWrapper">
        <PasteIcon />
        <span className="lexical-shortcutText">Paste</span>
      </div>
      <span className="lexical-shortcut">Ctrl+V</span>
    </div>
  );
}
